/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useCheckoutPricing } from '@recurly/react-recurly';
import Button from '@fff-web/fff-ui-shared/Button';
import {
  Address,
  Adjustment,
  ICodeInputHandleChangeCode,
  RecurlyError,
} from '../../../../interfaces';
import { ICouponInput } from 'packages/libs/fff-payments/src/interfaces/ICouponInput';
import { CI_FORM, CI_INPUT, CI_INPUT_WRAPPER, CI_LABEL } from './couponInputTestIds';
import { CTA_APPLY_COUPON } from '../../../utils/buttonCTA';
import FieldError from '../../Shared/FieldError';
import RecurlyWrapper from '../RecurlyWrapper';

import styles from './CouponInput.module.css';

const adjustments: Adjustment[] = [];
const address: Address = {
  first_name: '',
  last_name: '',
  country: '',
  postal_code: '',
};
const shippingAddress: Address = {
  first_name: '',
  last_name: '',
  country: '',
  postal_code: '',
};

//The whole component should be removed when we make the whole migration to the new API endpoint
//and we complete the migration to CouponInputV2

const CouponInputWrapped: React.FunctionComponent<ICouponInput> = ({
  plan,
  error,
  onCallbackError,
  onApply,
  initialCouponCode,
  forceLowercase,
}) => {
  const [coupon, setCoupon] = useState('');
  const [recurlyError, setRecurlyError] = useState<RecurlyError | null>(null);
  const [{ price, loading }, setPricing] = useCheckoutPricing({}, setRecurlyError);
  const initialPlan = useRef(plan);

  useEffect(() => {
    if (coupon && !loading && !recurlyError) {
      onApply({ coupon, price });
    }
  }, [loading]);

  useEffect(() => {
    if (initialCouponCode && initialCouponCode.length > 0) {
      setTimeout(() => testCoupon(initialCouponCode), 500);
    }

  }, [initialCouponCode]);

  useEffect(() => {
    if (initialPlan.current !== plan) {
      initialPlan.current = plan;
      handleSubmit();
    }
  }, [plan]);

  useEffect(() => {
    if (onCallbackError) {
      onCallbackError(recurlyError)
    }
  }, [recurlyError]);

  const testCoupon = (couponCode: string) => {
    setCoupon(couponCode);
    setRecurlyError(null);
    setPricing({
      coupon: couponCode,
      subscriptions: plan ? [{ plan }] : [],
      adjustments,
      address,
      shippingAddress,
    });
  };

  const handleSubmit = (e?: React.SyntheticEvent<HTMLFormElement>) => {
    e && e.preventDefault();
    testCoupon(coupon);
  };

  const handleChangeCode = ({ target: { value } }: ICodeInputHandleChangeCode) => {
    if (forceLowercase) {
      setCoupon(value && value.toLowerCase());
    } else {
      setCoupon(value);
    }
  };

  const invalidInputStyles = recurlyError ? ` ${styles.ciInvalid}` : '';

  return (
    <form data-testid={CI_FORM} onSubmit={handleSubmit} className={styles.ciContainer}>
      <label data-testid={CI_LABEL} htmlFor="couponCode" className={styles.ciLabel}>
        Enter Promo Code
        <div data-testid={CI_INPUT_WRAPPER} className={styles.ciInputWrapper}>
          <input
            data-testid={CI_INPUT}
            className={`${styles.ciInput}${invalidInputStyles}`}
            type="text"
            name="couponCode"
            placeholder="Promo Code"
            value={coupon}
            onChange={handleChangeCode}
          />
          <Button type="submit" disabled={loading} variation="primary-outline">
            {CTA_APPLY_COUPON}
          </Button>
        </div>
        {recurlyError ? (
          <FieldError errorMsg="This promo code is invalid" />
        ) : (
          error && error?.message && <FieldError errorMsg={error.message} />
        )}
      </label>
    </form>
  );
};

const CouponInput: React.FunctionComponent<ICouponInput> = ({
  plan,
  error,
  onApply,
  initialCouponCode,
  forceLowercase,
  onCallbackError
}) => (
  <RecurlyWrapper>
    <CouponInputWrapped {...{ plan, error, onApply, initialCouponCode, forceLowercase, onCallbackError }} />
  </RecurlyWrapper>
);

CouponInput.defaultProps = {
  error: {},
  onApply: () => ({}),
  initialCouponCode: null,
  plan: null,
  forceLowercase: true,
};

export default CouponInput;
